// The guide reference (issue #3708), as both of its consumers wire and record it: the Learn mic
// capture (pages/learn/audio/learn_mic_capture.ts) and the audio lab engine
// (pages/admin/engineering/audio_lab/audio_lab_engine.ts). Each plays every sound through one
// unity-gain guide bus to the speakers, and while the mic is open the bus also reaches the capture
// worklet's second input through a mono tap, so the worklet records the exact guide audio beside the
// microphone, sample for sample (capture_worklet.ts). A recording then has two channels, the
// microphone first and the guide second, the order the WAV convention in pitch/wav.ts names.
// No DOM access beyond the context handed in, so the pure parts run under Vitest and in Node.
//
// The wiring, in the order both consumers use it: createGuideBus() with the context, every sound into
// the bus; while the mic is open, a worklet node built with captureNodeOptions() and
// connectReference(), whose returned function takes the tap away again and leaves the bus on the
// speakers.

import { CaptureProcessorOptions } from './pitch/types.ts';

// What each channel of a two-channel recording holds, channel 0 first; the sidecars and session files
// name them in this order
export type RecordingChannel = 'microphone' | 'guide';

export const recordingChannels: RecordingChannel[] = ['microphone', 'guide'];

// The guide bus: a unity gain to the speakers. Every sound the page plays connects here, never to
// the destination itself, so the bus is the complete guide reference.
export function createGuideBus(context: BaseAudioContext): GainNode {
  const bus = context.createGain();
  bus.gain.value = 1;
  bus.connect(context.destination);
  return bus;
}

// The capture worklet node's options when it records the guide reference: input 1 is the reference,
// and the worklet reads it only because the processor option says so (capture_worklet.ts)
export function captureNodeOptions(windowSize: number, hopSize: number): AudioWorkletNodeOptions {
  const processorOptions: CaptureProcessorOptions = { windowSize, hopSize, reference: true };
  return { numberOfInputs: 2, numberOfOutputs: 1, outputChannelCount: [1], processorOptions };
}

// Feeds the guide bus to the worklet's second input through a mono tap and returns what takes it
// away again. The bus is attached to the tap last, so a browser that throws partway leaves nothing
// hanging off the guide. The returned function disconnects only what this connected - the bus from
// the tap, then the tap - each step guarded, because a teardown is no place to fail; the bus stays on
// the speakers, since the guide plays on after the mic has ended.
export function connectReference(context: BaseAudioContext, bus: AudioNode, worklet: AudioNode): () => void {
  const tap = createReferenceTap(context);
  tap.connect(worklet, 0, 1);
  bus.connect(tap);
  return () => {
    guarded(() => bus.disconnect(tap));
    guarded(() => tap.disconnect());
  };
}

function guarded(step: () => void) {
  try {
    step();
  } catch {
    // already disconnected, or this browser throws on tearing it down: nothing left to do
  }
}

// The tap between the guide bus and the worklet's second input. The worklet reads only the first
// channel of that input and the soundfont's notes may be stereo, so the tap is a unity GainNode with
// one explicit channel and speaker down-mixing: a stereo note is mixed down, not cut to its left
// channel. The microphone path and the worklet's own channel settings are left as they are.
export function createReferenceTap(context: BaseAudioContext): GainNode {
  const tap = context.createGain();
  tap.channelCount = 1;
  tap.channelCountMode = 'explicit';
  tap.channelInterpretation = 'speakers';
  return tap;
}

// One recorded chunk as the worklet sends it: `time` is the audio clock in seconds at `samples[0]`,
// and `reference` the guide for the same span, present when the node records one
export interface RecordedChunk {
  samples: Float32Array;
  reference?: Float32Array;
  time: number;
}

// The recording the chunks make, the two channels the same length and aligned sample for sample.
// `startTimeSec` is the audio clock at sample 0, undefined when there are no samples.
export interface JoinedRecording {
  samples: Float32Array; // the microphone
  reference: Float32Array; // the guide reference
  startTimeSec: number | undefined;
}

// The guide reference for exactly a chunk's span. The worklet sends one of the same length with every
// chunk; a chunk that came without one (or with a short one) is made up with silence, so the two
// channels never slide apart.
export function alignedReference(chunk: RecordedChunk): Float32Array {
  const { samples, reference } = chunk;
  if (reference?.length === samples.length) {
    return reference;
  }
  const aligned = new Float32Array(samples.length);
  if (reference) {
    aligned.set(reference.subarray(0, samples.length));
  }
  return aligned;
}

// Joins consecutive chunks, oldest first, into the two channels. The worklet streams chunks back to
// back while recording, so the first chunk's stamp is the whole recording's start.
export function joinRecordedChunks(chunks: RecordedChunk[]): JoinedRecording {
  const total = chunks.reduce((sum, chunk) => sum + chunk.samples.length, 0);
  const samples = new Float32Array(total);
  const reference = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    samples.set(chunk.samples, offset);
    reference.set(alignedReference(chunk), offset);
    offset += chunk.samples.length;
  }
  const first = chunks.find((chunk) => chunk.samples.length > 0);
  return { samples, reference, startTimeSec: first?.time };
}
